import React, { useState } from 'react'
import { get } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { adminUsersList, adminResetUserPasswordList } from '../actions/admin_users'
import {
  makeStyles,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import AdminMainLayout from './AdminMainLayout'
import { Separator } from '../../components/layout/Separator'

const useStyles = makeStyles((theme) => ({
  searchBox: {
    minWidth: '300px',
    marginRight: '10px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  searchButton: {
    height: '40px',
  },
  infoText: {
    color: '#666',
    fontSize: '14px',
    padding: '40px',
    textAlign: 'center',
  },
  tableContainer: {
    marginTop: '10px',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
  },
}))

export const AdminUsers = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const is_loading = useSelector(() => adminUsersList.isLoading())
  const users = useSelector(() => adminUsersList.getVisibleObjects())
  const pagination = useSelector(() => adminUsersList.getPagination())
  const [searchText, setSearchText] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  const handleSearchChange = (event) => {
    setSearchText(event.target.value)
  }

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  const handleSearch = () => {
    if (searchText.trim()) {
      setHasSearched(true)
      dispatch(adminUsersList.updateListFilter({ any_field: searchText }))
      dispatch(adminUsersList.invalidateList())
      dispatch(adminUsersList.fetchListIfNeeded())
    }
  }

  const handleChangePage = (event, newPage) => {
    dispatch(adminUsersList.updateListPagination({ page: newPage + 1 }))
    dispatch(adminUsersList.invalidateList())
    dispatch(adminUsersList.fetchListIfNeeded())
  }

  const handleViewUser = (user) => {
    setSelectedUser(user)
    setViewDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setViewDialogOpen(false)
    setSelectedUser(null)
  }

  const renderSearchBox = () => {
    return (
      <div className={classes.searchContainer}>
        <TextField
          className={classes.searchBox}
          placeholder="Search by email, first name, or last name..."
          value={searchText}
          onChange={handleSearchChange}
          onKeyPress={handleSearchKeyPress}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          className={classes.searchButton}
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={!searchText.trim()}
        >
          Search
        </Button>
      </div>
    )
  }

  const renderTable = () => {
    if (!hasSearched) {
      return (
        <Typography className={classes.infoText}>
          Enter a search term above to find users
        </Typography>
      )
    }

    if (is_loading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      )
    }

    if (users.length === 0) {
      return (
        <Typography className={classes.infoText}>
          No users found matching your search
        </Typography>
      )
    }

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Name Used</TableCell>
                <TableCell>Is Staff</TableCell>
                <TableCell>View</TableCell>
                <TableCell>Reset Password</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={get(user, 'id')}>
                  <TableCell>{get(user, 'id')}</TableCell>
                  <TableCell>{get(user, 'email')}</TableCell>
                  <TableCell>{get(user, 'first_name')}</TableCell>
                  <TableCell>{get(user, 'last_name')}</TableCell>
                  <TableCell>{get(user, 'name_used')}</TableCell>
                  <TableCell>{get(user, 'is_staff') ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <button onClick={() => handleViewUser(user)}>View</button>
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" color="secondary"
                      onClick={() => {
                        if (window.confirm(`Reset password for ${get(user, 'email')}?`)) {
                          dispatch(adminResetUserPasswordList.saveNewObject({user_id: get(user, 'id')}))
                            .then(() => alert('Password reset to boss1234$'))
                        }
                      }}
                    >
                      Reset Password
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.num_items || 0}
          page={(pagination.current_page || 1) - 1}
          onChangePage={handleChangePage}
          rowsPerPage={pagination.items_per_page || 10}
          rowsPerPageOptions={[10]}
        />
      </>
    )
  }

  return (
    <AdminMainLayout
      active_key='users'
      breadcrumbs={[
        { name: 'admin_home' },
        {
          name: 'users',
          label: 'Users',
          url: '/admin/users',
        },
      ]}
    >
      <Separator height={10} />
      {renderSearchBox()}
      <Paper variant="outlined" square className={classes.tableContainer}>
        {renderTable()}
      </Paper>

      <Dialog open={viewDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          User Details
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {get(selectedUser, 'first_name')} {get(selectedUser, 'last_name')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  User ID
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'id') || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'email') || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Name Used
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'name_used') || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Is Staff
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'is_staff') ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Phone Number
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'phone_number') || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Watermark
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'watermark') || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Admin Charge
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'initial_price.admin_charge') ?? 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Commission Percentage
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'initial_price.commission_percentage') != null
                    ? `${get(selectedUser, 'initial_price.commission_percentage')}%`
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Referral Percentage
                </Typography>
                <Typography variant="body1">
                  {get(selectedUser, 'initial_price.referral_percentage') != null
                    ? `${get(selectedUser, 'initial_price.referral_percentage')}%`
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </AdminMainLayout>
  )
}
